import { Card, Stack, Title, Text } from '@repletoryreach/sdk-ui';
import { Alert, Timeline } from '@repletoryreach/sdk-ui/data-display';
import type { admin } from '@repletoryreach/sdk-api';
import { formatDateTime } from '../../../../lib/format';
import { useStatusLabel } from '../../components/RunStatusBadge';
import { formatDuration } from '../../lib/duration';
import { useT } from '../../../../i18n/I18nProvider';

type FlowRun = NonNullable<Awaited<ReturnType<typeof admin.listFlowRuns>>['data']>[number];

// Per-node trace persistence ships with the flow builder (W12). Until then the
// tab shows the two boundaries the projection does record, plus the failure
// reason, which is the one thing an operator needs before the trace store
// exists.
export function TraceTab({ run }: { run: FlowRun }) {
  const t = useT();
  const statusLabel = useStatusLabel();
  const finished = Boolean(run.finished_at);

  return (
    <Stack gap="lg">
      <Alert color="info" variant="light">
        {t('flows.view.trace.notice')} <code>started_at</code> / <code>finished_at</code>.
      </Alert>

      <Card withBorder>
        <Stack gap="md">
          <Title order={4}>{t('flows.view.trace.title')}</Title>
          <Timeline active={finished ? 1 : 0}>
            <Timeline.Item title={t('flows.view.trace.started')}>
              <Text role="caption" c="var(--rr-color-text-muted)">{formatDateTime(run.started_at)}</Text>
            </Timeline.Item>
            <Timeline.Item
              title={finished
                ? t('flows.view.trace.finished', { status: statusLabel(run.status) })
                : t('flows.view.trace.running', { status: statusLabel(run.status) })}
            >
              <Stack gap="xs">
                <Text role="caption" c="var(--rr-color-text-muted)">
                  {run.finished_at
                    ? `${formatDateTime(run.finished_at)} · ${formatDuration(run.started_at, run.finished_at)}`
                    : t('flows.view.run.runningFor', { duration: formatDuration(run.started_at, null) })}
                </Text>
                {run.error && (
                  <Stack gap="xs">
                    <Text role="caption" c="var(--rr-color-text-muted)">{t('flows.view.trace.errorLabel')}</Text>
                    <Text role="bodySmall" c="var(--rr-color-error)">{run.error}</Text>
                  </Stack>
                )}
              </Stack>
            </Timeline.Item>
          </Timeline>
        </Stack>
      </Card>
    </Stack>
  );
}
